import { cva, type VariantProps } from "class-variance-authority";
import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@/shared/lib/cn";

/**
 * The primary action primitive. Feature code chooses a semantic variant and
 * never reaches for a color or elevation token directly.
 */
export const buttonVariants = cva(
  "studio-button inline-flex items-center justify-center gap-1.5 rounded-button text-center font-sans font-medium tracking-[0.005em] outline-none transition-[background-color,color,transform,opacity] duration-150 ease-house focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] disabled:cursor-not-allowed disabled:opacity-55 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // `accent` remains as a compatibility name for the shared primary action.
        accent:
          "studio-action bg-action-bg text-action-text hover:bg-action-bg-hover active:bg-action-bg-active",
        ink: "studio-secondary-button text-ink",
        glass: "studio-glass-button text-ink",
        secondary: "studio-secondary-button text-ink",
        image: "studio-action bg-[var(--badge-image)] text-white [--button-glow:var(--media-image)] hover:bg-[var(--button-image-hover)]",
        video: "studio-action bg-[var(--badge-video)] text-[var(--media-video-on)] [--button-glow:var(--media-video)] hover:bg-[var(--button-video-hover)]",
        voice: "studio-action bg-[var(--badge-voice)] text-[var(--media-voice-on)] [--button-glow:var(--media-voice)] hover:bg-[var(--button-voice-hover)]",
        danger:
          "studio-action bg-danger-solid text-white [--button-glow:var(--danger)] hover:bg-danger-hover active:bg-[var(--danger-active)] focus-visible:outline-danger",
        dangerSolid:
          "studio-action bg-danger-solid text-white [--button-glow:var(--danger)] hover:bg-danger-hover active:bg-[var(--danger-active)] focus-visible:outline-danger",
        ghost: "bg-transparent text-ink-soft hover:text-ink focus-visible:text-ink active:text-ink",
        outline: "studio-outline border border-line-strong bg-transparent text-ink hover:border-ink-soft hover:bg-hover",
        subtle:
          "bg-transparent text-ink-muted hover:text-ink focus-visible:text-ink active:text-ink",
      },
      size: {
        sm: "min-h-11 px-3.5 py-1.5 text-xs sm:min-h-8",
        md: "min-h-11 px-4 py-2 text-[13px] sm:min-h-9",
        lg: "min-h-12 px-5 py-2.5 text-sm",
      },
    },
    defaultVariants: { variant: "ink", size: "md" },
  },
);

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Disables the action and exposes its pending state without changing width. */
  loading?: boolean;
  /** Optional visible replacement for the button label while loading. */
  loadingLabel?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant,
      size,
      type = "button",
      loading = false,
      loadingLabel,
      disabled,
      children,
      ...props
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        {...props}
        data-finish="satin"
        data-variant={variant ?? "ink"}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        className={cn(buttonVariants({ variant, size }), className)}
      >
        {loading ? (
          <span
            aria-hidden="true"
            className="size-3.5 shrink-0 animate-spin rounded-full border-[1.5px] border-current border-e-transparent motion-reduce:animate-none"
          />
        ) : null}
        {loading && loadingLabel !== undefined ? loadingLabel : children}
      </button>
    );
  },
);
